import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "@/components/site/Page";

const TITLE = "Genomgång inför flytten – Swedish International School";
const DESCRIPTION =
  "Ingår om ni vill, för familjer som anmäler minst ett barn på heltid en termin: 45 minuter om skolstarten och det praktiska kring flytten till Costa del Sol.";

export const Route = createFileRoute("/flyttgenomgang")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/flyttgenomgang") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/flyttgenomgang"),
  }),
  component: RelocationPage,
});

function RelocationPage() {
  return (
    <>
      <PageHero
        eyebrow="Ingår i ansökan"
        title="45 minuters genomgång inför flytten"
        intro="Genomgången ingår om ni önskar och gäller er som anmäler minst ett barn på heltid, minst en termin. När ni är antagna och inskrivningsavgiften är betald bjuder vi in familjen till en personlig genomgång på cirka 45 minuter där vi går igenom skolstarten och det praktiska kring livet på Costa del Sol."
      />

      <Prose>
        <p>
          Att flytta utomlands med barn innebär många beslut på kort tid. Vi har tagit emot familjer
          från hela Norden i många år och ser samma frågor återkomma. Därför erbjuder vi varje ny
          familj en genomgång där ni får ställa era frågor till någon som känner både skolan och
          området.
        </p>

        <h2>Det här går vi igenom</h2>
        <p>
          Vi går igenom praktiska saker som är bra att tänka på inför flytten och det som väntar
          när ni väl är på plats, och svarar på era frågor kring allt från skolstarten till
          vardagslivet. Vad genomgången tar upp beror alltid på hur er situation ser ut – inga
          familjer är lika.
        </p>

        <h2>Så går det till</h2>
        <ul>
          <li>Ingår om ni önskar för familjer med minst ett barn på heltid, minst en termin – den ingår inte vid korttidsplats.</li>
          <li>Ni kryssar i att ni vill ha genomgången i anmälningsformuläret, så hör vi av oss.</li>
          <li>Sker tidigast 15 dagar efter påskrivet avtal och genomförd betalning.</li>
          <li>Cirka 45 minuter, på plats på skolan eller digitalt.</li>
          <li>En gång per familj – oavsett hur många barn ni anmäler.</li>
        </ul>

        <h2>Bra att veta</h2>
        <p>
          Genomgången är allmän vägledning från skolan utifrån vår erfarenhet av att ta emot
          inflyttade familjer. Den ersätter inte juridisk, skatterättslig eller ekonomisk rådgivning,
          och vi förmedlar inte bostäder. Behöver ni specialiststöd kan vi peka er i rätt riktning.
        </p>

        <p>
          <Link className="text-primary link-quiet" to="/ansokan">
            Gör en ansökan
          </Link>{" "}
          eller{" "}
          <Link className="text-primary link-quiet" to="/avgifter">
            läs mer om avgifterna
          </Link>
          .
        </p>
      </Prose>

      <div className="pb-16 sm:pb-24">
        <CtaBand
          title="Vill ni ha genomgången?"
          text="Kryssa i rutan i anmälningsformuläret så bokar vi in en tid när ni är antagna. Gäller er med minst ett barn på heltid, minst en termin."
        />
      </div>
    </>
  );
}
